import { throttle } from 'lodash-es';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useScroll } from 'react-use';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature } from 'src/WebApiController';
import useResizeObserver from 'use-resize-observer';

interface UseSetTopListItemPositionProps {
  topItemListRef: MutableRefObject<HTMLDivElement | null>;
  virtuosoId: string;
}

/**
 * Virtuoso's list wrapper (virtuoso-item-list) has paddingTop to separate
 * from sticky header. When Virtuoso component TopListItem changes its height,
 * virtuoso-item-list doesn't update it's padding.
 * This hook, maintains that paddingTop update to date.
 */
export const useSetTopListItemStyle = (
  { topItemListRef, virtuosoId }: UseSetTopListItemPositionProps,
  disabled: boolean //  * TODO: Remove flag when NoScrollDependencyOnVirtuoso is GA'ed
) => {
  const virtuosoItemListSelector = useMemo(() => {
    return `[id="${virtuosoId}"] [data-testid="virtuoso-item-list"]`;
  }, [virtuosoId]);

  const extraTransformRef = useRef(0);

  const updateVirtuosoItemListPaddingTop = useCallback(() => {
    if (disabled) {
      return;
    }
    requestAnimationFrame(() => {
      const topItemListEl = topItemListRef.current;
      const virtuosoItemListEl = document.querySelector(
        virtuosoItemListSelector
      ) as HTMLDivElement;
      if (!topItemListEl || !virtuosoItemListEl) {
        return;
      }
      const previousPaddingTop = parseFloat(
        window.getComputedStyle(virtuosoItemListEl).paddingTop
      );
      const gap = topItemListEl.offsetHeight - previousPaddingTop;
      // Transform the extra gap for the extra height of the top list item.
      // This is to prevent the jump when the top list item height increases.
      // The gap will be removed when the scroll happens and the paddingTop is updated.
      if (gap !== extraTransformRef.current && gap >= 0) {
        virtuosoItemListEl.style.transform = `translateY(${gap}px)`;
        extraTransformRef.current = gap;
      }
    });
  }, [disabled, topItemListRef, virtuosoItemListSelector]);

  const throttledHandler = useMemo(
    () => throttle(updateVirtuosoItemListPaddingTop, 300),
    [updateVirtuosoItemListPaddingTop]
  );

  useEffect(() => {
    const virtuosoScrollerEl = document.getElementById(
      virtuosoId
    ) as HTMLDivElement;

    if (!virtuosoScrollerEl) {
      return;
    }

    virtuosoScrollerEl.addEventListener('scroll', throttledHandler);

    return () => {
      throttledHandler.cancel();
      virtuosoScrollerEl.removeEventListener('scroll', throttledHandler);
    };
  }, [virtuosoId, throttledHandler]);

  useResizeObserver<HTMLDivElement>({
    ref: topItemListRef,
    onResize: throttledHandler,
  });
};

/**
 * Hook to get the scroll reference and the scroll y position.
 * The scroll y position is only returned if the user does not have the NoScrollDependencyOnVirtuoso feature
 *
 * TODO: clean this hook when NoScrollDependencyOnVirtuoso is enabled.
 * @returns scrollRef - reference to the scrollable element
 * @returns scrollY - current scroll y position of the scrollable element, or undefined if the user has the NoScrollDependencyOnVirtuoso feature
 */
export const useVirtualizedScroll = () => {
  const hasNoScrollDependencyOnVirtuosoFeature = useUserHasFeature(
    Feature.NoScrollDependencyOnVirtuoso
  );
  const scrollRef = useRef<HTMLElement | null>(null);
  const scrollState = useScroll(scrollRef);

  const scrollY = useMemo(() => {
    if (hasNoScrollDependencyOnVirtuosoFeature) {
      return undefined;
    }
    return scrollState.y;
  }, [hasNoScrollDependencyOnVirtuosoFeature, scrollState.y]);

  return { scrollRef, scrollY };
};
